import React, { useEffect } from 'react';

export interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  title?: string;
}

const Modal: React.FC<ModalProps> = ({ isOpen, onClose, children, title }) => {
  // Add ESC key handler
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 backdrop-blur z-50 flex items-center justify-center"
      style={{ backgroundColor: 'var(--modal-backdrop)' }}
      onClick={onClose}
      aria-modal="true"
      role="dialog"
    >
      <div 
        className="theme-surface rounded-lg shadow-2xl p-5 w-full max-w-lg relative z-60 transition-all duration-200"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex justify-between items-center border-b pb-2 mb-3">
          <div>
            <h2 className="text-xl font-semibold text-indigo-500">{title || 'Modal'}</h2>
            <p className="text-xs theme-text-muted mt-1">
              Press <kbd className="px-1 py-0.5 theme-muted border theme-border rounded text-xs">ESC</kbd> to close
            </p>
          </div>
          <button 
            onClick={onClose}
            className="theme-text-muted hover:theme-text focus:outline-none focus:ring-2 focus:ring-indigo-400"
            aria-label="Close modal (ESC)"
          >
            &times;
          </button>
        </div>
        <div>
          {children}
        </div>
      </div>
    </div>
  );
};

export default Modal; 